import React, {useCallback} from 'react'
import {useSelector} from 'react-redux'
import {AnchorSimilarHotels} from 'components/AnchorSimilarHotels'
import {CashbackInfoBottomSheetContainer} from 'components/Cashback/CashbackInfoBottomSheetContainer'
import {useUrlUpdater} from 'components/data/WithUrlUpdater'
import {
  DealFreezeEducationBanner,
  DealFreezeEducationBannerProvider
} from 'components/DealFreezeEducationBanner'
import {BANNER_PLACEMENT} from 'components/DealFreezeEducationBanner/constants'
import {FrozenDealToast} from 'components/FrozenDealToast'
import {HopperInlineBanner} from 'components/HopperAds'
import {HostelsAlert} from 'components/HostelsAlert'
import {HotelAnchorHiddenAlert} from 'components/HotelAnchorHiddenAlert'
import {MLosAlert} from 'components/MLosAlert'
import {SearchResultAlert} from 'components/SearchResultAlert'
import {SeoStructuredData} from 'components/SeoSRP/SeoStructuredData'
import {SplitBookingInfoBottomSheet} from 'components/SplitBookings/SplitBookingInfoBottomSheet'
import {HotelPriceStructuredData} from 'components/StructuredDataMarkup'
import {TrustpilotBottomSheet} from 'components/Trustpilot/TrustpilotBottomSheet'
import {VisibilitySensor} from 'components/VisibilitySensor'
import {OfferViewContext, OfferViewType} from 'context/offerViewContext'
import {useDestinationDisplayName} from 'hooks/useDestinationDisplayName'
import {useIsSeoSRP} from 'hooks/useIsSeoSRP'
import {useSeoBreadcrumb} from 'hooks/useSeoBreadcrumb'
import {getResolvedAnchorHotelId} from 'modules/sapiSearch/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {Breadcrumbs} from '@vio/shared/src/whiteLabel/Breadcrumbs/Breadcrumbs'
import {PoweredBy} from '@vio/shared/src/whiteLabel/PoweredBy/PoweredBy'

import {SeoBreadcrumb} from '../SeoSRP/SeoBreadcrumb'
import {SeoHeading} from '../SeoSRP/SeoHeading'
import {AnchorHotel} from './SearchResultsAnchorHotel'
import {SearchResultsControls} from './SearchResultsControls'
import {SearchResultsList} from './SearchResultsList'
import useResultsEndSensor from './useResultsEndSensor'

const HostelsAlertWrapped: React.FC = () => {
  const {updateLocation} = useUrlUpdater()
  return <HostelsAlert onChange={updateLocation} />
}

export const SearchResults: React.FC = () => {
  const isSeoSRP = useIsSeoSRP()
  const {isResultsEndVisibilitySensorEnabled, onResultsEndView} =
    useResultsEndSensor()
  const {
    commerce: {coBranded}
  } = useBrandCapabilities()
  const isCoBranded = coBranded.enabled
  const {isMobile, isDesktopXs} = useDeviceLayout()
  const destinationDisplayName = useDestinationDisplayName()
  const breadcrumb = useSeoBreadcrumb()
  const anchorHotelId = useSelector(getResolvedAnchorHotelId)
  const isAnchorScenario = Boolean(anchorHotelId)

  // Generate the current search destination for the breadcrumb link
  const placeOfSearch = destinationDisplayName
    ? {
        label: destinationDisplayName
      }
    : undefined

  const onVisibilityChange = useCallback(
    isVisible => {
      if (isVisible) onResultsEndView()
    },
    [onResultsEndView]
  )

  const isMobileHotelCard = isMobile || isDesktopXs

  return (
    <DealFreezeEducationBannerProvider>
      <VisibilitySensor
        partialVisibility="bottom"
        active={isResultsEndVisibilitySensorEnabled}
        onChange={onVisibilityChange}
      >
        <div>
          <MLosAlert />
          <SearchResultAlert />
          <HostelsAlertWrapped />
          {isCoBranded && (
            <Box
              display="flex"
              alignItems="center"
              gap="s500"
              justifyContent="space-between"
              paddingBottom="s300"
              paddingLeft={!isMobile ? 's500' : 's100'}
              paddingRight={!isMobile ? 's400' : 's000'}
              marginRight={!isMobile ? 's200' : 's000'}
            >
              <Breadcrumbs placeOfSearch={placeOfSearch} />
              <PoweredBy />
            </Box>
          )}
          {!isAnchorScenario && <HopperInlineBanner />}
          <HotelAnchorHiddenAlert hotelId={anchorHotelId} />
          <DealFreezeEducationBanner slot={BANNER_PLACEMENT.primary} />
          <SearchResultsControls />
          {isSeoSRP && (
            <>
              <SeoBreadcrumb breadcrumb={breadcrumb} />
              <SeoHeading />
              <SeoStructuredData />
            </>
          )}
          {isAnchorScenario && (
            <>
              {/* Added as part of 46507954-hotel-price-structured-data */}
              <HotelPriceStructuredData />
              <AnchorHotel anchorHotelId={anchorHotelId} />
              {/* Added for fec27873-similar-hotels */}
              <AnchorSimilarHotels />
              <HopperInlineBanner disableMargins />
              <DealFreezeEducationBanner slot={BANNER_PLACEMENT.secondary} />
            </>
          )}
          <OfferViewContext.Provider value={OfferViewType.SRP}>
            <SearchResultsList />
          </OfferViewContext.Provider>
          {isMobileHotelCard && <SplitBookingInfoBottomSheet />}
          {isMobileHotelCard && <CashbackInfoBottomSheetContainer />}
          {isMobileHotelCard && <TrustpilotBottomSheet />}

          <FrozenDealToast />
        </div>
      </VisibilitySensor>
    </DealFreezeEducationBannerProvider>
  )
}
